// Winner (1x2) - Movement — 1x2 odds drift for one outcome (Phase 3 → M2 wave
// 10). Thin wrapper over <MovementChart>. No scope; the outcome labels are built
// from the fixture team names ("<Home> to Win" / "Draw" / "<Away> to Win"),
// matching the legacy renderer. Controls: bookmaker + market line (outcome).
//
// The series + offsets now arrive from app-api via `useWidgetData` as one
// `WinnerMovementPayload` envelope `{ series, bkOffsets }` (was the
// getWinner1x2MovementData / getBkOffsets getters); the controls reshape the
// fetched payload client-side (no refetch).

import { shapeMovement } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import MovementChart from "./MovementChart.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface Winner1x2MovementProps {
  fixture: string;
  bookmaker?: string | null;
  /** Outcome toggle: "home" | "draw" | "away"; defaults to "home". */
  marketLine?: string | null;
}

export default function Winner1x2Movement({
  fixture,
  bookmaker,
  marketLine,
}: Winner1x2MovementProps) {
  const fix = fixture || "Arsenal v Everton";
  const { data, isPending, isError, refetch } = useWidgetData(
    "winner-1x2-movement",
    useFixtureKey(fix)
  );
  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const key = marketLine || "home";
  const series = data?.series ?? {};
  const fixData = series[fix] ?? series["Arsenal v Everton"];
  const base = fixData?.[key] ?? fixData?.["home"] ?? [];
  const offset = (data?.bkOffsets ?? {})[bookmaker || "Pinnacle"] ?? 0;
  const parts = fix.split(" v ");
  const t1 = parts[0] || "Home";
  const t2 = parts[1] || "Away";
  const labels: Record<string, string> = {
    home: t1 + " to Win",
    draw: "Draw",
    away: t2 + " to Win",
  };
  const shape = shapeMovement(base, offset, labels[key] ?? key);
  if (!shape) return <div className="w1x2m-wrapper bm-wrapper" />;
  return <MovementChart wrapperClass="w1x2m-wrapper" shape={shape} />;
}
